(() => {
  'use strict';

  if (customElements.get('custom-bt-model-selection')) return;

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'details > summary:first-of-type',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  class CustomBtModelSelection extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.track = this.querySelector('[data-model-track]');
      this.items = Array.from(this.querySelectorAll('[data-model-item]'));
      this.modelPreviousButtons = Array.from(this.querySelectorAll('[data-model-prev]'));
      this.modelNextButtons = Array.from(this.querySelectorAll('[data-model-next]'));
      this.modelStatuses = Array.from(this.querySelectorAll('[data-model-status]'));
      this.modelLiveRegion = this.querySelector('[data-model-live]');
      this.dialog = this.querySelector('[data-model-dialog]');
      this.dialogContent = this.dialog?.querySelector('[data-dialog-content]') || null;
      this.dialogPreviousButtons = Array.from(this.dialog?.querySelectorAll('[data-dialog-prev]') || []);
      this.dialogNextButtons = Array.from(this.dialog?.querySelectorAll('[data-dialog-next]') || []);
      this.dialogStatuses = Array.from(this.dialog?.querySelectorAll('[data-dialog-status]') || []);
      this.activeIndex = 0;
      this.lastTrigger = null;
      this.scrollFrame = null;
      this.resizeFrame = null;
      this.initialFrame = null;
      this.focusFrame = null;
      this.scrollTargetIndex = null;
      this.scrollTargetTimer = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.trackDescriptionId = this.track?.getAttribute('aria-describedby') || '';
      this.preloadedColorImages = new Set();
      this.colorPreloadObserver = null;

      if (!this.track || !this.items.length) return;

      this.querySelectorAll('[data-detail-open]').forEach((button) => {
        button.removeAttribute('tabindex');
        button.removeAttribute('aria-disabled');
      });
      this.dataset.enhanced = 'true';
      this.setViewMode(this.dataset.viewMode === 'list');
      this.addEventListener('click', this.handleClick.bind(this), { signal: this.signal });
      this.addEventListener('keydown', this.handleComponentKeydown.bind(this), { signal: this.signal });
      this.track.addEventListener('scroll', this.queueScrollSync.bind(this), { passive: true, signal: this.signal });
      this.track.addEventListener('focusin', this.handleTrackFocus.bind(this), { signal: this.signal });
      this.track.addEventListener('pointerdown', this.clearProgrammaticScroll.bind(this), { signal: this.signal });
      this.track.addEventListener('wheel', this.clearProgrammaticScroll.bind(this), { passive: true, signal: this.signal });
      window.addEventListener('resize', this.queueResizeSync.bind(this), { passive: true, signal: this.signal });
      document.addEventListener('shopify:block:select', this.handleBlockSelect.bind(this), { signal: this.signal });
      this.setupColorPreloading();

      if (this.dialog) {
        this.dialog.setAttribute('aria-modal', 'true');
        this.dialog.addEventListener('cancel', this.handleDialogCancel.bind(this), { signal: this.signal });
        this.dialog.addEventListener('close', this.handleDialogClose.bind(this), { signal: this.signal });
      }

      this.updateNavigation(false);
      this.initialFrame = requestAnimationFrame(() => {
        this.initialFrame = null;
        this.goTo(0, { behavior: 'auto', announce: false });
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.cancelFrames();
      this.colorPreloadObserver?.disconnect();
      this.colorPreloadObserver = null;

      if (this.dialog?.open) this.dialog.close();
      this.dialogContent?.replaceChildren();
      this.lastTrigger = null;
      this.releasePageLock();
      delete this.dataset.enhanced;
    }

    cancelFrames() {
      if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);
      if (this.resizeFrame !== null) cancelAnimationFrame(this.resizeFrame);
      if (this.initialFrame !== null) cancelAnimationFrame(this.initialFrame);
      if (this.focusFrame !== null) cancelAnimationFrame(this.focusFrame);
      if (this.scrollTargetTimer !== null) window.clearTimeout(this.scrollTargetTimer);

      this.scrollFrame = null;
      this.resizeFrame = null;
      this.initialFrame = null;
      this.focusFrame = null;
      this.scrollTargetTimer = null;
      this.scrollTargetIndex = null;
    }

    handleClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const variantLink = target.closest('a[data-variant-link][data-variant-id]');
      if (variantLink && this.contains(variantLink)) this.addVariantToLink(variantLink);

      const control = target.closest(
        '[data-color-option], [data-model-prev], [data-model-next], [data-detail-open], [data-dialog-prev], [data-dialog-next], [data-dialog-close], [data-dialog-back]'
      );

      if (control && !this.contains(control)) return;
      if (control?.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }

      if (control?.matches('[data-color-option]')) {
        event.preventDefault();
        this.selectColor(control);
      } else if (control?.matches('[data-model-prev]')) {
        event.preventDefault();
        this.goTo(this.activeIndex - 1);
      } else if (control?.matches('[data-model-next]')) {
        event.preventDefault();
        this.goTo(this.activeIndex + 1);
      } else if (control?.matches('[data-detail-open]')) {
        event.preventDefault();
        const item = control.closest('[data-model-item]');
        const index = this.items.indexOf(item);
        if (index >= 0) this.openDetails(index, control);
      } else if (control?.matches('[data-dialog-prev]')) {
        event.preventDefault();
        this.goTo(this.activeIndex - 1);
      } else if (control?.matches('[data-dialog-next]')) {
        event.preventDefault();
        this.goTo(this.activeIndex + 1);
      } else if (control?.matches('[data-dialog-close], [data-dialog-back]')) {
        event.preventDefault();
        this.closeDetails();
      } else if (event.target === this.dialog) {
        this.closeDetails();
      }
    }

    selectColor(control) {
      const item = control.closest('[data-model-item]');
      if (!item) return;

      item.querySelectorAll('[data-color-option]').forEach((option) => {
        option.setAttribute('aria-pressed', option === control ? 'true' : 'false');
      });

      const selectedLabel = item.querySelector('[data-selected-color-label]');
      if (selectedLabel) selectedLabel.textContent = control.dataset.colorLabel || '';

      this.applyVariantData(item, control);
      this.preloadColorOption(control);

      const index = this.items.indexOf(item);
      if (index === this.activeIndex && this.dialog?.open) this.renderDialog(index);
    }

    applyVariantData(scope, option) {
      if (!scope || !option) return;

      this.updateImage(
        scope.querySelector('[data-card-image]'),
        option.getAttribute('data-card-image-src'),
        option.getAttribute('data-card-image-srcset'),
        option.getAttribute('data-color-image-alt')
      );
      this.updateImage(
        scope.querySelector('[data-detail-image]'),
        option.getAttribute('data-detail-image-src'),
        option.getAttribute('data-detail-image-srcset'),
        option.getAttribute('data-color-image-alt')
      );

      const variantId = option.getAttribute('data-color-variant-id');
      if (variantId) {
        scope.querySelectorAll('[data-variant-link]').forEach((link) => {
          link.setAttribute('data-variant-id', variantId);
        });
      }

      const currentPrice = option.getAttribute('data-color-price-current') || '';
      const comparePrice = option.getAttribute('data-color-price-compare') || '';
      const available = option.getAttribute('data-color-available') === 'true';

      scope.querySelectorAll('[data-variant-price]').forEach((price) => {
        price.classList.toggle('custom-bt-model-selection__price--sale', Boolean(comparePrice));

        const current = price.querySelector('[data-variant-price-current]');
        const compare = price.querySelector('[data-variant-price-compare]');
        const availability = price.querySelector('[data-variant-availability]');

        if (current) current.textContent = currentPrice;
        if (compare) {
          compare.textContent = comparePrice;
          compare.hidden = !comparePrice;
        }
        if (availability) availability.hidden = available;
      });
    }

    addVariantToLink(link) {
      const variantId = link.getAttribute('data-variant-id');
      if (!variantId) return;
      const url = new URL(link.href, window.location.href);
      url.searchParams.set('variant', variantId);
      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    }

    updateImage(image, src, srcset, alt) {
      if (!(image instanceof HTMLImageElement)) return;

      if (srcset) image.setAttribute('srcset', srcset);
      if (src) image.setAttribute('src', src);
      if (alt) image.setAttribute('alt', alt);
    }

    setupColorPreloading() {
      const itemsWithColors = this.items.filter((item) => item.querySelector('[data-color-option]'));
      if (!itemsWithColors.length) return;

      const preloadItem = (item) => {
        item.querySelectorAll('[data-color-option]').forEach((option) => this.preloadColorOption(option));
      };

      if (!('IntersectionObserver' in window)) {
        itemsWithColors.forEach(preloadItem);
        return;
      }

      this.colorPreloadObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            preloadItem(entry.target);
            this.colorPreloadObserver?.unobserve(entry.target);
          });
        },
        { rootMargin: '160px 0px' }
      );

      itemsWithColors.forEach((item) => this.colorPreloadObserver.observe(item));
    }

    preloadColorOption(option) {
      const src = option.getAttribute('data-card-image-src');
      const srcset = option.getAttribute('data-card-image-srcset');
      const cacheKey = srcset || src;
      if (!cacheKey || this.preloadedColorImages.has(cacheKey)) return;

      const preload = new Image();
      preload.decoding = 'async';
      preload.sizes = '(min-width: 1200px) 23vw, (min-width: 750px) 46vw, 86vw';
      if (srcset) preload.srcset = srcset;
      if (src) preload.src = src;
      this.preloadedColorImages.add(cacheKey);
    }

    handleComponentKeydown(event) {
      if (event.key === 'Escape' && this.dialog?.open) {
        event.preventDefault();
        this.closeDetails();
        return;
      }

      if (event.key === 'Tab' && this.dialog?.open) {
        this.trapDialogFocus(event);
        return;
      }

      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      const target = event.target instanceof Element ? event.target : null;
      const mainKeyboardTarget = target === this.track || target?.closest('[data-model-prev], [data-model-next]');
      const dialogKeyboardTarget = target === this.dialog || target?.closest('[data-dialog-prev], [data-dialog-next]');
      if (!mainKeyboardTarget && !dialogKeyboardTarget) return;

      event.preventDefault();
      this.goTo(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }

    handleTrackFocus(event) {
      if (this.isListView()) return;

      const target = event.target instanceof Element ? event.target : null;
      const item = target?.closest('[data-model-item]');
      const index = this.items.indexOf(item);
      if (index >= 0 && index !== this.activeIndex) {
        this.goTo(index, { behavior: 'auto' });
      }
    }

    handleBlockSelect(event) {
      const sectionId = String(event.detail?.sectionId || '');
      if (sectionId && this.dataset.sectionId && sectionId !== this.dataset.sectionId) return;

      const target = event.target instanceof Element ? event.target : null;
      let item = target?.closest('[data-model-item]') || null;

      if (!item || !this.contains(item)) {
        const blockId = String(event.detail?.blockId || '');
        item = this.items.find((candidate) => candidate.dataset.blockId === blockId) || null;
      }

      const index = this.items.indexOf(item);
      if (index < 0) return;

      this.goTo(index, { behavior: 'auto', announce: false });
    }

    goTo(index, options = {}) {
      if (!this.items.length) return;

      const nextIndex = Math.min(this.items.length - 1, Math.max(0, Number(index) || 0));
      const changed = nextIndex !== this.activeIndex;
      this.activeIndex = nextIndex;
      this.updateNavigation(options.announce ?? changed);

      if (options.scroll !== false && !this.isListView()) {
        this.scrollToItem(nextIndex, options.behavior);
      }

      if (this.dialog?.open && options.syncDialog !== false) {
        this.renderDialog(nextIndex);
      }
    }

    scrollToItem(index, requestedBehavior) {
      const item = this.items[index];
      if (!this.track || !item) return;

      const trackRect = this.track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const styles = getComputedStyle(this.track);
      const scrollPadding = Number.parseFloat(styles.scrollPaddingInlineStart);
      const padding = Number.parseFloat(styles.paddingInlineStart);
      const inset = Number.isFinite(scrollPadding) ? scrollPadding : Number.isFinite(padding) ? padding : 0;
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const desired = this.track.scrollLeft + itemRect.left - trackRect.left - inset;
      const left = Math.min(maximum, Math.max(0, desired));
      const behavior = requestedBehavior || (this.reducedMotion.matches ? 'auto' : 'smooth');

      this.scrollTargetIndex = index;
      if (this.scrollTargetTimer !== null) window.clearTimeout(this.scrollTargetTimer);
      this.scrollTargetTimer = window.setTimeout(() => {
        this.scrollTargetIndex = null;
        this.scrollTargetTimer = null;
      }, behavior === 'smooth' ? 700 : 80);

      this.track.scrollTo({ left, behavior });
    }

    clearProgrammaticScroll() {
      this.scrollTargetIndex = null;
      if (this.scrollTargetTimer !== null) window.clearTimeout(this.scrollTargetTimer);
      this.scrollTargetTimer = null;
    }

    isListView() {
      return this.dataset.viewMode === 'list';
    }

    setViewMode(showAsList) {
      const listViewEnabled = Boolean(showAsList);
      this.dataset.viewMode = listViewEnabled ? 'list' : 'carousel';

      if (!this.track) return;

      this.clearProgrammaticScroll();
      if (listViewEnabled) {
        this.track.removeAttribute('tabindex');
        this.track.removeAttribute('aria-describedby');
        return;
      }

      this.track.setAttribute('tabindex', '0');
      if (this.trackDescriptionId) this.track.setAttribute('aria-describedby', this.trackDescriptionId);
      this.goTo(this.activeIndex, { behavior: 'auto', announce: false, syncDialog: false });
    }

    queueScrollSync() {
      if (this.scrollFrame !== null || this.dialog?.open || this.isListView()) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.syncIndexFromScroll();
      });
    }

    syncIndexFromScroll() {
      if (!this.track || !this.items.length || this.dialog?.open || this.isListView()) return;

      const trackRect = this.track.getBoundingClientRect();
      const styles = getComputedStyle(this.track);
      const scrollPadding = Number.parseFloat(styles.scrollPaddingInlineStart);
      const padding = Number.parseFloat(styles.paddingInlineStart);
      const inset = Number.isFinite(scrollPadding) ? scrollPadding : Number.isFinite(padding) ? padding : 0;
      const start = trackRect.left + inset;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.items.forEach((item, index) => {
        const distance = Math.abs(item.getBoundingClientRect().left - start);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      if (this.scrollTargetIndex !== null && nearestIndex !== this.scrollTargetIndex) return;
      if (nearestIndex === this.scrollTargetIndex) this.clearProgrammaticScroll();
      this.goTo(nearestIndex, { scroll: false, syncDialog: false });
    }

    queueResizeSync() {
      if (this.resizeFrame !== null) return;

      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.clearProgrammaticScroll();
        this.goTo(this.activeIndex, { behavior: 'auto', announce: false, syncDialog: false });
      });
    }

    updateNavigation(announce = true) {
      const count = this.items.length;
      const atStart = !count || this.activeIndex === 0;
      const atEnd = !count || this.activeIndex === count - 1;

      this.setDisabled(this.modelPreviousButtons, atStart);
      this.setDisabled(this.modelNextButtons, atEnd);
      this.setDisabled(this.dialogPreviousButtons, atStart);
      this.setDisabled(this.dialogNextButtons, atEnd);

      const modelLabel = this.dataset.modelLabel || 'Modell';
      const ofLabel = this.dataset.ofLabel || 'von';
      const status = `${modelLabel} ${count ? this.activeIndex + 1 : 0} ${ofLabel} ${count}`;
      const modelName = this.items[this.activeIndex]?.dataset.modelName || '';

      this.modelStatuses.forEach((element) => {
        element.textContent = status;
      });
      this.dialogStatuses.forEach((element) => {
        element.textContent = status;
        if (modelName) {
          const hiddenName = document.createElement('span');
          hiddenName.className = 'visually-hidden';
          hiddenName.textContent = `: ${modelName}`;
          element.append(hiddenName);
        }
      });

      if (announce && this.modelLiveRegion) {
        this.modelLiveRegion.textContent = modelName ? `${status}: ${modelName}` : status;
      }

      this.items.forEach((item, index) => {
        item.toggleAttribute('data-active', index === this.activeIndex);
      });
    }

    setDisabled(elements, disabled) {
      elements.forEach((element) => {
        element.removeAttribute('disabled');
        element.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      });
    }

    openDetails(index, trigger) {
      if (!this.dialog || !this.dialogContent) return;
      if (!this.renderDialog(index)) return;

      this.lastTrigger = trigger;
      this.goTo(index, { behavior: 'auto', announce: true, syncDialog: false });

      if (!this.dialog.open) {
        document.body.classList.add('custom-bt-model-selection-is-open');
        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }
      }

      if (this.focusFrame !== null) cancelAnimationFrame(this.focusFrame);
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = null;
        const closeButton = this.dialog?.querySelector('[data-dialog-close]');
        this.focusElement(closeButton || this.dialog);
      });
    }

    renderDialog(index) {
      const item = this.items[index];
      const template = item?.querySelector('template[data-model-detail-template]');
      if (!template?.content || !this.dialog || !this.dialogContent) return false;

      this.dialogContent.replaceChildren(template.content.cloneNode(true));
      const selectedColor = item.querySelector('[data-color-option][aria-pressed="true"]');
      if (selectedColor) this.applyVariantData(this.dialogContent, selectedColor);
      this.dialogContent.scrollTop = 0;
      this.dialog.scrollTop = 0;
      this.dialog
        .querySelector('[data-dialog-scroll], .custom-bt-model-selection__dialog-scroll')
        ?.scrollTo({ top: 0, behavior: 'auto' });

      this.updateNavigation(false);
      return true;
    }

    closeDetails() {
      if (!this.dialog) return;

      if (this.dialog.open && typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.handleDialogClose();
      }
    }

    handleDialogCancel(event) {
      event.preventDefault();
      this.closeDetails();
    }

    handleDialogClose() {
      this.releasePageLock();

      const returnTarget = this.lastTrigger?.isConnected
        ? this.lastTrigger
        : this.items[this.activeIndex]?.querySelector('[data-detail-open]') || this.track;
      this.lastTrigger = null;
      this.focusElement(returnTarget);
    }

    releasePageLock() {
      const anotherDialogIsOpen = document.querySelector(
        'custom-bt-model-selection [data-model-dialog][open]'
      );
      if (!anotherDialogIsOpen) document.body.classList.remove('custom-bt-model-selection-is-open');
    }

    trapDialogFocus(event) {
      const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => element.getAttribute('aria-hidden') !== 'true' && !element.closest('[inert]') && element.getClientRects().length
      );

      if (!focusable.length) {
        event.preventDefault();
        this.focusElement(this.dialog);
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!this.dialog.contains(active) || active === this.dialog) {
        event.preventDefault();
        this.focusElement(event.shiftKey ? last : first);
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        this.focusElement(last);
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        this.focusElement(first);
      }
    }

    focusElement(element) {
      if (!(element instanceof HTMLElement)) return;

      try {
        element.focus({ preventScroll: true });
      } catch (_error) {
        element.focus();
      }
    }
  }

  customElements.define('custom-bt-model-selection', CustomBtModelSelection);
})();
